import { Dispatch, FC, RefObject, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import { useOnClickOutside } from 'usehooks-ts';

import CalendarSearch from '@/assets/icons/CalendarSearch';
import { getDaySelectionVariations } from '@/utils/dateVariations';

import ChipButton from '../../v1Overwrites/ChipButton/ChipButton';
import SearchButton from '../SearchButton/SearchButton';
import SearchButtonAutoFocus from '../SearchButton/SearchButtonAutoFocus';
import PanelList from '../SearchPanel/PanelList';
import SearchPanelLi from '../SearchPanel/SearchPanelLi';

import iconStyles from '../../styles/searchIcons.module.css';
import styles from './SearchDate.module.css';

type SearchDateProps = {
  searchDate: string | undefined | null;
  setSearchDate: (date: string | null) => void | Dispatch<SetStateAction<string | null>>;
  focusInput: (boolean?: boolean) => void;
};
/**
 * Known bugs: When user clicks outside the option field, without selecting a option. It does not set 'open' to false correctly.
 */
const SearchDate: FC<SearchDateProps> = ({ searchDate, setSearchDate, focusInput }) => {
  const [preSelectedIndex, setPreSelectedIndex] = useState<number>(0);
  const [open, setOpen] = useState<boolean>(false);
  const divRef = useRef<HTMLDivElement>(null);

  const options = useMemo(() => {
    const dates = getDaySelectionVariations(2);
    return dates.map((date, index) => ({ index, label: date.shortDescription, value: date.date }));
  }, []);

  useEffect(() => {
    setPreSelectedIndex(options.find((option) => option.value === (searchDate ?? ''))?.index ?? 0);
  }, [options, searchDate]);

  const toggleDateSelection = () => {
    let newValue: boolean = false;
    setOpen((prev) => {
      newValue = !prev;
      return newValue;
    });
    focusInput(newValue);
  };
  const removeSelectedSearchDate = () => setSearchDate(null);

  useOnClickOutside(divRef as RefObject<HTMLElement>, () => setOpen(false));

  return (
    <div ref={divRef} className={styles.searchDateContainer} style={{ height: '24px' }}>
      <div style={{ maxHeight: '24px' }}>
        {searchDate ? (
          <ChipButton
            testid='searchDateSelected'
            size='small'
            transparentHover
            handleOnClose={removeSelectedSearchDate}
            onClick={toggleDateSelection}
            label={options.find((variant) => variant.value === searchDate)?.label ?? ''}
          />
        ) : (
          <SearchButton
            tabIndex={0}
            size='auto'
            data-testid='selectSearchDateButton'
            onClick={toggleDateSelection}
            onKeyDown={(event) => {
              if (!open && event.key === 'Enter') {
                setOpen(true);
                focusInput(true);
              }
              if (open && event.key === 'Escape') setOpen(false);
            }}
          >
            <CalendarSearch className={iconStyles.calendarIcon} />
          </SearchButton>
        )}
      </div>

      {open && (
        <div className={styles.searchDateList}>
          <PanelList
            data={options}
            style={{ padding: '8px' }}
            row={(option, index) => (
              <SearchPanelLi
                index={Number(index)}
                returnSelectedListItem={() => {
                  setSearchDate(option.value || null);
                  setOpen(false);
                }}
                selectedListIndex={preSelectedIndex}
                key={`search_date_${JSON.stringify(option)}`}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 500 }}>
                  <SearchButtonAutoFocus
                    size='block'
                    style={{ textAlign: 'left', fontSize: '16px' }}
                    autoFocus={preSelectedIndex === index}
                    onClick={() => {
                      setSearchDate(option.value || null);
                      setOpen(false);
                      focusInput(true);
                    }}
                    onKeyDown={(event) => {
                      if (!open) return;
                      if (['Enter', 'ArrowDown', 'ArrowUp'].includes(event.key)) {
                        event.preventDefault();
                        event.stopPropagation();
                      }
                      if (event.key === 'ArrowDown')
                        setPreSelectedIndex((prev) => (prev + 1 <= options.length - 1 ? prev + 1 : options.length - 1));
                      if (event.key === 'ArrowUp') setPreSelectedIndex((prev) => (prev - 1 >= 0 ? prev - 1 : 0));
                      if (event.key === 'Enter') {
                        setSearchDate(option.value || null);
                        setOpen(false);
                        focusInput(true);
                      }
                      if (event.key === 'Escape') setOpen(false);
                    }}
                  >
                    {option.label}
                  </SearchButtonAutoFocus>
                </div>
              </SearchPanelLi>
            )}
          />
        </div>
      )}
    </div>
  );
};
export default SearchDate;
